import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  List,
  Divider,
  Collapse,
  Typography,
  useMediaQuery,
  ListItemButton,
  useTheme,
} from '@mui/material'
import { CommonButton } from '@/components/common'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

export const DonationSidebar = ({
  items = [],
  title = 'Where to Donate:',
  selectedId,
  onChange,
  autoCollapse = false,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [expanded, setExpanded] = useState(true)
  const prevAutoCollapseRef = useRef(false)

  useEffect(() => {
    if (!isMobile) {
      setExpanded(true)
    }
  }, [isMobile])

  useEffect(() => {
    if (!isMobile) return
    if (autoCollapse && !prevAutoCollapseRef.current) {
      setExpanded(false)
    }
    prevAutoCollapseRef.current = autoCollapse
  }, [autoCollapse, isMobile])

  return (
    <Box
      data-testid='donation-sidebar'
      sx={{
        width: '100%',
        borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          variant='h6'
          sx={{
            mb: { xs: 0, md: 1.5 },
            typography: { xs: 'subtitle1', md: 'h6' },
          }}
        >
          {title}
        </Typography>
        <CommonButton
          iconOnly={<KeyboardArrowDownIcon fontSize='small' />}
          variant='text'
          size='small'
          aria-label='toggle sidebar'
          aria-expanded={expanded}
          onClick={() => setExpanded(prev => !prev)}
          sx={{
            display: { xs: 'inline-flex', md: 'none' },
            ml: 0.5,
            transition: 'transform 150ms ease',
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        />
      </Box>
      <Collapse in={expanded} timeout='auto'>
        <List id='donation-sidebar-list' disablePadding>
          {items.map((item, idx) => (
            <Box key={item.id}>
              <ListItemButton
                onClick={() => onChange && onChange(item.id)}
                selected={item.id === selectedId}
                id={`donation-sidebar-${item.id}`}
                data-testid={`donation-sidebar-${item.id}`}
                sx={theme => ({
                  mt: { xs: 0.5, md: 1 },
                  mb: { xs: 0.5, md: 1 },
                  borderRadius: { xs: '6px', md: '8px' },
                  py: { xs: 1, md: 1.25 },
                  px: { xs: 1, md: 2 },
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.secondary,
                  },
                })}
              >
                <Typography
                  variant='body1'
                  sx={theme => ({
                    fontWeight: 600,
                    fontSize: {
                      xs: theme.typography.body2.fontSize,
                      md: theme.typography.body1.fontSize,
                    },
                  })}
                >
                  {item.label}
                </Typography>
              </ListItemButton>
              <Divider
                sx={{
                  display: {
                    xs: 'block',
                    md: idx < items.length - 1 ? 'block' : 'none',
                  },
                }}
              />
            </Box>
          ))}
        </List>
      </Collapse>
    </Box>
  )
}

DonationSidebar.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      render: PropTypes.func,
    })
  ),
  title: PropTypes.node,
  selectedId: PropTypes.string,
  onChange: PropTypes.func,
  autoCollapse: PropTypes.bool,
}
